import { useMemo } from "react";
import type { TrackLane } from "@midi-fantom-utils/midi-util";
import { DRUM_COLOR, channelColor } from "../channelColors.js";
import type { UseProgramMapResult } from "../useProgramMap.js";
import s from "./TrackList.module.css";

/**
 * Список дорожек слева от лейнового таймлайна (Bitwig-стиль). Одна строка на
 * дорожку: цвет-точка, имя, число нот, кнопка Mute (M) и видимости (глаз),
 * для не-ударных — селектор GM-инструмента.
 *
 * Вертикальный скролл синхронизирован с `TrackLanes` через `scrollY` (тот же
 * value, что приходит из `onScrollYChange` лейнов): строки сдвигаются
 * `translateY(-scrollY)` внутри контейнера с `overflow: hidden`. Ширина фикс,
 * высота = высота лейнов (`height`).
 */

export type TrackListProps = {
  lanes: ReadonlyArray<TrackLane>;
  /** Заглушенные дорожки (Set id). */
  mutedLanes: ReadonlySet<string>;
  /** Видимые дорожки (Set id). undefined = все. */
  enabledLanes?: ReadonlySet<string>;
  /** GM-программа на канал (см. `channelProgram`). */
  programs: ReadonlyMap<number, number>;
  programMap: UseProgramMapResult;
  /** Вертикальный скролл лейнов (px). */
  scrollY: number;
  /** Высота одной строки-дорожки, px (должна совпадать с laneHeight лейнов). */
  laneHeight: number;
  /** Высота контейнера списка, px. */
  height: number;
  onToggleMute: (laneId: string) => void;
  onToggleVisible: (laneId: string) => void;
  onInstrumentChange: (channel: number, program: number) => void;
};

export const TrackList = ({
  lanes,
  mutedLanes,
  enabledLanes,
  programs,
  programMap,
  scrollY,
  laneHeight,
  height,
  onToggleMute,
  onToggleVisible,
  onInstrumentChange,
}: TrackListProps) => {
  const labelOf = useMemo(() => {
    const map = new Map<number, string>();
    for (const m of programMap.melodic) {
      if (!map.has(m.program)) map.set(m.program, m.name);
    }
    return map;
  }, [programMap.melodic]);

  const currentName = (lane: TrackLane): string => {
    if (lane.isDrum) return programMap.drumKit ?? "Drums";
    return labelOf.get(lane.program) ?? "";
  };

  const handleInstrumentChange = (lane: TrackLane, name: string) => {
    const prog = programMap.reverse.get(name);
    if (prog !== undefined) onInstrumentChange(lane.channel, prog);
  };

  return (
    <div className={s.list} style={{ height }} data-testid="track-list">
      <div className={s.head}>Дорожки</div>
      <div className={s.viewport}>
        <div
          className={s.rows}
          style={{ transform: `translateY(-${scrollY}px)` }}
          data-testid="track-list-rows"
        >
          {lanes.map((lane) => {
            const visible = enabledLanes ? enabledLanes.has(lane.id) : true;
            const muted = mutedLanes.has(lane.id);
            const color = lane.isDrum ? DRUM_COLOR : channelColor(lane.channel);
            const name = currentName(lane);
            return (
              <div
                key={lane.id}
                className={`${s.row} ${visible ? "" : s.rowHidden} ${muted ? s.rowMuted : ""}`}
                style={{ height: laneHeight }}
                data-lane-id={lane.id}
                data-testid="track-list-row"
              >
                <span
                  className={s.dot}
                  style={{ background: color }}
                  aria-hidden="true"
                />
                <span className={s.name} title={lane.name}>
                  {name || lane.name}
                </span>
                <span className={s.count} title={`${lane.noteCount} нот`}>
                  {lane.noteCount}
                </span>
                <button
                  type="button"
                  className={`${s.btn} ${muted ? s.btnActive : ""}`}
                  onClick={() => onToggleMute(lane.id)}
                  aria-pressed={muted}
                  data-testid="track-list-mute"
                  title={muted ? "Снять mute" : "Mute (не играть при воспроизведении)"}
                >
                  M
                </button>
                <button
                  type="button"
                  className={`${s.btn} ${visible ? s.btnActive : ""}`}
                  onClick={() => onToggleVisible(lane.id)}
                  aria-pressed={visible}
                  data-testid="track-list-visible"
                  title={visible ? "Скрыть дорожку" : "Показать дорожку"}
                >
                  ◉
                </button>
                {lane.isDrum ? (
                  <span
                    className={s.drumKit}
                    data-testid="track-list-drum-kit"
                    title={`Ударные: ${programMap.drumKit ?? "не найдены"}`}
                  >
                    🥁
                  </span>
                ) : (
                  <select
                    className={s.select}
                    value={name}
                    disabled={programMap.loading}
                    onChange={(e) => handleInstrumentChange(lane, e.target.value)}
                    data-testid="track-list-instrument-select"
                    title={`Инструмент канала ${lane.channel} (GM program ${lane.program})`}
                  >
                    {name ? null : <option value="">—</option>}
                    {programMap.melodic.map((m) => (
                      <option key={m.program} value={m.name}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
